import { privateApi } from '@/config/axios.config';

// Configuración de la API
// const API_BASE_URL = APP_CONFIG.api.baseUrl;

// Interfaz para los datos de creación de agente
export interface CreateAgentData {
  // Información personal
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
  };
  city: string;
  country: string;
  avatar?: File;
  avatarStorageId?: string;

  // Información profesional
  role: 'agent' | 'broker' | 'assistant';
  licenseNumber?: string;
  experience: string;
  specialties: string[];
  languages: string[];

  // Configuración de cuenta
  status: 'active' | 'inactive';
  isFeatured: boolean;
  canManageProperties: boolean;
  canManageClients: boolean;
  canViewReports: boolean;

  // Asignaciones iniciales
  assignedProperties: string[];
  assignedClients: string[];
  notes?: string;
}

// Interfaz para la respuesta de la API
export interface AgentResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
}

// Los headers de autenticación se manejan automáticamente por privateApi

// Servicio para crear un nuevo agente
export const createAgent = async (
  agentData: CreateAgentData
): Promise<AgentResponse> => {
  try {

    const response = await privateApi.post('/agency/agents', agentData);


    return response.data;
  } catch (error: any) {
    // Extraer mensaje de error más específico
    let errorMessage = 'Error al crear el agente';

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Lanzar error con información estructurada
    throw {
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
      originalError: error,
    };
  }
};

// Servicio para actualizar un agente existente
export const updateAgent = async (
  agentId: string,
  agentData: Partial<CreateAgentData>
): Promise<AgentResponse> => {
  try {

    const response = await privateApi.patch(
      `/agency/agents/${agentId}`,
      agentData
    );


    return response.data;
  } catch (error: any) {
    // Extraer mensaje de error más específico
    let errorMessage = 'Error al actualizar el agente';

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Lanzar error con información estructurada
    throw {
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
      originalError: error,
    };
  }
};

// Servicio para obtener la lista de agentes
export const getAgents = async (): Promise<any> => {
  try {
    const response = await privateApi.get('/agency/agents');
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Servicio para obtener un agente por ID
export const getAgentById = async (id: string): Promise<any> => {
  try {
    const response = await privateApi.get(`/agency/agents/${id}`);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// GH#1826 — invitar al agente a la plataforma. Sin body: el correo es el de la ficha,
// tenant + quién invita salen del JWT. 201 = enviada (o reenviada si estaba pendiente).
// Lanza { status, code, message } para que la UI muestre el mensaje en español correcto
// (429 INVITACION_LIMITE, 404, 409 AGENTE_YA_TIENE_CUENTA / EMAIL_YA_ES_USUARIO_DEL_TENANT
// / EMAIL_EN_USO).
export interface InviteAgentError {
  status?: number;
  code?: string;
  message: string;
  data?: unknown;
}

export const inviteAgent = async (agentId: string): Promise<any> => {
  try {
    const response = await privateApi.post(`/agency/agents/${agentId}/invite`);
    return response.data;
  } catch (error: any) {
    throw {
      status: error.response?.status,
      code: error.response?.data?.code,
      message:
        error.response?.data?.message ??
        error.message ??
        'Error al enviar la invitación',
      data: error.response?.data,
    } as InviteAgentError;
  }
};

// Servicio para eliminar un agente
export const deleteAgent = async (id: string): Promise<any> => {
  try {
    const response = await privateApi.delete(`/agency/agents/${id}`);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Servicio para obtener agentes para dropdown con búsqueda
export const getAgentsForDropdown = async (searchTerm?: string, limit: number = 20): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (searchTerm) {
      params.append('q', searchTerm);
    }
    params.append('limit', limit.toString());

    const response = await privateApi.get(`/agency/agents/dropdown?${params.toString()}`);

    // Manejar diferentes estructuras de respuesta
    let agents = response.data;

    // Si la respuesta tiene una propiedad 'data' o 'agents', extraerla
    if (agents && typeof agents === 'object' && !Array.isArray(agents)) {
      if (agents.data && Array.isArray(agents.data)) {
        agents = agents.data;
      } else if (agents.agents && Array.isArray(agents.agents)) {
        agents = agents.agents;
      } else if (agents.results && Array.isArray(agents.results)) {
        agents = agents.results;
      }
    }

    // Asegurar que devolvemos un array
    return Array.isArray(agents) ? agents : [];
  } catch (error: any) {
    throw error;
  }
};
